<template>
	<div class="co-mask" @click.self="closeWindow">
		<section
			id="confirmOrderWindow"
			class="co-dialog"
			role="dialog"
			aria-modal="true"
			:aria-label="$t('purchase_confirm_text')"
		>
			<div class="co-header">
				<h3 class="co-title">{{ $t('purchase_confirm_text') }}</h3>
				<button class="co-close" type="button" :aria-label="$t('close_text')" @click="closeWindow">
					<span aria-hidden="true">&times;</span>
				</button>
			</div>

			<div class="co-body">
				<section class="co-order-summary co-checkout-module">
					<div class="co-role-row">
						<span class="co-role-avatar" aria-hidden="true"><i class="fa fa-user"></i></span>
						<div class="co-role-copy">
							<strong class="co-role-name">{{ roleInfo.roleName }}</strong>
							<span class="co-role-meta">ID: {{ roleInfo.roleId }}<span class="co-sep">&middot;</span>{{ roleInfo.serverName }}</span>
						</div>
					</div>

					<div class="co-product-row">
						<div class="co-product-main">
							<div class="co-product-icon-wrap">
								<img v-if="goods.icon" :src="goods.icon" class="co-product-icon" :alt="goods.tips || ''">
								<i v-else class="fa fa-ticket co-product-icon-fallback" aria-hidden="true"></i>
							</div>
							<div class="co-product-text">
								<strong class="co-product-name">{{ goods.tips }}</strong>
								<span v-if="goods.points > 0" class="co-product-pts">{{ $t('earn_points_text') }}: {{ goods.points }}</span>
							</div>
						</div>
						<div class="co-product-price-area">
							<strong class="co-product-price">{{ goods.currency }} {{ formatMoney(goods.amount) }}</strong>
							<span v-if="finalRebate > 0" class="co-rebate-note">
								{{ rebate.mode == 3 ? $t('rebate_limited_text', { pct: finalRebate }) : $t('rebate_percent_text', { pct: finalRebate }) }}
							</span>
						</div>
					</div>
				</section>

				<section
					v-if="!checkoutReady || displayCoupons.length > 0"
					class="co-section co-coupon-section co-checkout-module"
					:aria-busy="!checkoutReady"
				>
					<div v-if="!checkoutReady" class="co-coupon-skeleton" aria-hidden="true">
						<span class="co-skeleton-shape co-skeleton-line co-skeleton-coupon-label"></span>
						<span class="co-skeleton-shape co-skeleton-value"></span>
						<span class="co-skeleton-shape co-skeleton-chevron"></span>
					</div>
					<button
						v-else
						class="co-coupon-select co-data-ready"
						:class="[
							{ 'co-coupon-select-empty': !selectedCouponObj },
							selectedCouponObj ? 'is-' + getCouponKind(selectedCouponObj) : ''
						]"
						type="button"
						@click="openCouponPicker"
					>
						<strong class="co-coupon-entry-label">
							<span class="co-coupon-entry-icon" aria-hidden="true"><i class="fa fa-gift"></i></span>
							<span>{{ $t('coupon_entry_label') }}</span>
						</strong>
						<span class="co-coupon-summary">
							<em v-if="selectedCouponObj" class="co-coupon-kind-tag">
								{{ getCouponKindLabel(getCouponKind(selectedCouponObj)) }}
							</em>
							<span class="co-coupon-value" :class="selectedCouponObj ? 'is-' + getCouponKind(selectedCouponObj) : ''">
								{{ selectedCouponObj
									? getCouponValueText(selectedCouponObj)
									: $t('coupon_available_count', { count: availableCoupons.length }) }}
							</span>
						</span>
						<i class="fa fa-chevron-right co-coupon-arrow" aria-hidden="true"></i>
					</button>

					<p v-if="showExtraRebateTips" class="co-coupon-notice">{{ $t('coupon_extra_rebate_notice') }}</p>
				</section>

				<section
					v-if="!checkoutReady || recommendedChannels.length || otherChannels.length"
					class="co-section co-payment-section co-checkout-module"
					:aria-busy="!checkoutReady"
				>
					<div class="co-section-heading">
						<div class="co-section-title-wrap">
							<span class="co-section-icon" aria-hidden="true"><i class="fa fa-star"></i></span>
							<h4>{{ $t('payment_recommended_text') }}</h4>
						</div>
					</div>

					<template v-if="!checkoutReady">
						<div class="co-pay-group co-pay-group--recommended co-pay-group-skeleton" aria-hidden="true">
							<div v-for="index in 3" :key="'recommended-skeleton-' + index" class="co-channel co-channel-skeleton">
								<span class="co-skeleton-shape co-skeleton-logo"></span>
								<span class="co-skeleton-channel-copy">
									<span class="co-skeleton-shape co-skeleton-line co-skeleton-channel-name"></span>
									<span class="co-skeleton-shape co-skeleton-line co-skeleton-benefit"></span>
								</span>
								<span class="co-skeleton-shape co-skeleton-radio"></span>
							</div>
						</div>
						<div class="co-pay-group co-pay-group--other co-pay-group-skeleton" aria-hidden="true">
							<div class="co-section-heading co-payment-group-heading co-payment-group-heading--muted">
								<div class="co-section-title-wrap">
									<span class="co-section-icon"><i class="fa fa-credit-card"></i></span>
									<span class="co-skeleton-shape co-skeleton-group-title"></span>
								</div>
							</div>
							<div v-for="index in 2" :key="'other-skeleton-' + index" class="co-channel co-channel-skeleton">
								<span class="co-skeleton-shape co-skeleton-logo"></span>
								<span class="co-skeleton-channel-copy">
									<span class="co-skeleton-shape co-skeleton-line co-skeleton-channel-name"></span>
									<span class="co-skeleton-shape co-skeleton-line co-skeleton-benefit"></span>
								</span>
								<span class="co-skeleton-shape co-skeleton-radio"></span>
							</div>
						</div>
					</template>

					<div
						v-else-if="recommendedChannels.length"
						class="co-pay-group co-pay-group--recommended co-data-ready"
						role="group"
						:aria-label="$t('payment_recommended_text')"
					>
						<button
							v-for="item in recommendedChannels"
							:key="'rec-' + item._idx"
							class="co-channel"
							:class="{ 'co-channel-on': selectedChannel === item._idx }"
							type="button"
							:aria-pressed="selectedChannel === item._idx"
							@click="chooseChannel(item._idx)"
						>
							<img class="co-ch-icon" :class="item.iconStyle === 'rectangle' ? 'co-ch-rect' : 'co-ch-sq'" :src="item.icon" :alt="item.name">
							<span class="co-ch-copy">
								<span class="co-ch-name">{{ item.name }}</span>
								<span v-if="getChannelBenefitText(item)" class="co-ch-benefit">{{ getChannelBenefitText(item) }}</span>
							</span>
							<span class="co-ch-radio" :class="{ on: selectedChannel === item._idx }"><i class="fa fa-check" aria-hidden="true"></i></span>
						</button>
					</div>

					<div
						v-if="checkoutReady && otherChannels.length"
						class="co-pay-group co-pay-group--other co-data-ready"
						role="group"
						:aria-label="$t('payment_other_methods_text')"
					>
						<div class="co-section-heading co-payment-group-heading co-payment-group-heading--muted">
							<div class="co-section-title-wrap">
								<span class="co-section-icon" aria-hidden="true"><i class="fa fa-credit-card"></i></span>
								<h4>{{ $t('payment_other_methods_text') }}</h4>
							</div>
						</div>
						<button
							v-for="item in otherChannels"
							:key="'other-' + item._idx"
							class="co-channel"
							:class="{ 'co-channel-on': selectedChannel === item._idx }"
							type="button"
							:aria-pressed="selectedChannel === item._idx"
							@click="chooseChannel(item._idx)"
						>
							<img class="co-ch-icon" :class="item.iconStyle === 'rectangle' ? 'co-ch-rect' : 'co-ch-sq'" :src="item.icon" :alt="item.name">
							<span class="co-ch-copy">
								<span class="co-ch-name">{{ item.name }}</span>
								<span v-if="getChannelBenefitText(item)" class="co-ch-benefit">{{ getChannelBenefitText(item) }}</span>
							</span>
							<span class="co-ch-radio" :class="{ on: selectedChannel === item._idx }"><i class="fa fa-check" aria-hidden="true"></i></span>
						</button>
					</div>
				</section>
			</div>
			<div class="co-footer">
				<div class="co-price-summary">
					<div class="co-price-breakdown">
						<span class="co-price-item">
							{{ $t('order_amount_text') }}
							<strong>{{ goods.currency }} {{ formatMoney(goods.amount) }}</strong>
						</span>
						<span v-if="selectedDiscountCoupon && selectedDiscountAmount > 0" class="co-price-item co-price-discount">
							{{ $t('discount_amount_text') }}
							<strong>-{{ goods.currency }} {{ formatMoney(selectedDiscountAmount) }}</strong>
						</span>
					</div>
					<div class="co-total-row">
						<span>{{ $t('payable_amount_text') }}</span>
						<strong>{{ goods.currency }} {{ formatMoney(payableAmount) }}</strong>
					</div>
				</div>
				<button
					id="confirmButton"
					class="co-confirm-btn bg_color"
					:disabled="buttonDisabled || !checkoutReady || !currentChannel"
					type="button"
					@click="openThirdPaymentPage"
				>
					{{ payButtonText }}
				</button>
			</div>

			<transition name="co-picker-fade">
				<div v-if="showCouponPicker" class="co-picker-overlay" @click.self="closeCouponPicker">
					<div class="co-picker-sheet" role="dialog" aria-modal="true" :aria-label="$t('coupon_picker_title')">
						<div class="co-picker-handle"></div>
						<div class="co-picker-header">
							<strong class="co-picker-title">{{ $t('coupon_picker_title') }}</strong>
							<button class="co-picker-close" type="button" :aria-label="$t('close_text')" @click="closeCouponPicker">
								×
							</button>
						</div>

						<div class="co-picker-tabs" role="tablist" :aria-label="$t('coupon_picker_title')">
							<button
								v-for="tab in couponKindTabs"
								:key="tab.kind"
								class="co-picker-tab"
								:class="[
									'is-' + tab.kind,
									{ 'is-active': activeCouponKind === tab.kind, 'has-selection': selectedCouponKind === tab.kind }
								]"
								type="button"
								role="tab"
								:disabled="tab.count === 0"
								:aria-selected="activeCouponKind === tab.kind"
								@click="setActiveCouponKind(tab.kind)"
							>
								<span>{{ tab.label }}</span>
								<small>{{ tab.count }}</small>
							</button>
						</div>

						<div class="co-picker-list">
							<section
								v-for="group in couponPickerGroups"
								:key="group.key"
								class="co-picker-group"
								:class="{ 'co-picker-group--unavailable': group.disabled }"
							>
								<button
									v-if="group.disabled"
									class="co-picker-unavailable-toggle"
									type="button"
									:aria-expanded="showUnavailableCoupons"
									@click="showUnavailableCoupons = !showUnavailableCoupons"
								>
									<span>{{ $t('coupon_unavailable_title') }}</span>
									<small>{{ group.items.length }}{{ $t('coupon_count_unit') }}</small>
									<i :class="showUnavailableCoupons ? 'fa fa-caret-up' : 'fa fa-caret-down'" aria-hidden="true"></i>
								</button>

								<div v-show="!group.disabled || showUnavailableCoupons" class="co-picker-group-list">
									<button
										v-for="item in group.items"
										:key="item.couponId"
										class="co-picker-row"
										:class="[
											'co-picker-row--' + activeCouponKind,
											{
												'co-picker-row-on': isCouponSelected(item),
												'co-picker-row-disabled': group.disabled
											}
										]"
										type="button"
										:disabled="group.disabled"
										:aria-pressed="isCouponSelected(item)"
										@click="selectCoupon(item)"
									>
										<span class="co-picker-check" :class="{
											on: isCouponSelected(item),
											'is-disabled': group.disabled
										}" aria-hidden="true"></span>
										<span class="co-picker-row-info">
											<strong>{{ getCouponLabel(item) }}<em v-if="isBestCoupon(item)" class="co-picker-best-tag">{{ $t('coupon_best_tag') }}</em></strong>
											<small>{{ getCouponPickerHint(item) }}</small>
										</span>
										<span class="co-picker-value" :class="'is-' + activeCouponKind">{{ getCouponValueText(item) }}</span>
									</button>
								</div>
							</section>
						</div>
					</div>
				</div>
			</transition>
		</section>
	</div>
</template>

<script>
import axios from 'axios'
import {
	isEmpty,
	isRebateApplicable,
	calculateRebatePercent
} from '../utils/index.js'
import { openThirdPartyPayment } from '../utils/payment.js'

const DISCOUNT_AMOUNT_FIELDS = [
	'localDiscountAmount',
	'discountAmount',
	'reduceAmount',
	'deductionAmount',
	'amountOff',
	'faceValue',
	'couponAmount',
	'fixedUsdDiscountAmount'
]

export default {
	name: 'confirmOrder',
	props: {
		goods: {
			type: Object,
			default: () => ({})
		},
		rebate: {
			type: Object,
			default: () => ({})
		},
		coupon: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			gameData: this.$store.state.gameData,
			roleInfo: this.$cache.getRoleInfo() || {},
			loginData: this.$cache.getLoginData() || {},
			srcChannelList: [],
			channelList: [],
			selectedChannel: -1,
			payButtonText: this.$i18n.tc('payment_button_text'),
			buttonDisabled: false,
			coupons: [],
			couponLoading: true,
			channelLoading: true,
			checkoutReady: false,
			selectedCouponId: null,
			activeCouponKind: 'discount',
			couponSelectionTouched: false,
			showCouponPicker: false,
			showUnavailableCoupons: true,
			timer: null
		}
	},
	watch: {
		goods: {
			immediate: true,
			handler(newVal) {
				if (!isEmpty(newVal)) {
					this.channelList = this.filterChannelList(this.srcChannelList)
					if (this.checkoutReady) {
						this.selectDefaultChannel()
						this.$nextTick(() => this.reconcileCouponSelection(true))
					}
				}
			}
		}
	},
	computed: {
		isSuperCouponEnabled() {
			const extraFields = this.gameData && this.gameData.extraFields
			return !!extraFields && extraFields.enableSuperCoupon === true
		},
		currentChannel() {
			return this.selectedChannel >= 0 ? this.channelList[this.selectedChannel] || null : null
		},
		selectedCouponObj() {
			if (this.selectedCouponId === null || this.selectedCouponId === undefined) return null
			return this.availableCoupons.find(item => this.sameCouponId(item.couponId, this.selectedCouponId)) || null
		},
		selectedDiscountCoupon() {
			return this.selectedCouponObj && this.getCouponKind(this.selectedCouponObj) === 'discount'
				? this.selectedCouponObj
				: null
		},
		selectedDiscountAmount() {
			return this.selectedDiscountCoupon ? this.getCouponSavingAmount(this.selectedDiscountCoupon) : 0
		},
		payableAmount() {
			const amount = this.toNumber(this.goods.amount, 0)
			const couponPayableAmount = this.selectedDiscountCoupon
				? this.toNumber(this.selectedDiscountCoupon.payableLocalAmount)
				: null
			if (couponPayableAmount !== null) return Math.max(0, couponPayableAmount)
			return Math.max(0, amount - this.selectedDiscountAmount)
		},
		availableCoupons() {
			return this.coupons.filter(coupon => this.isCouponApplicable(coupon, this.currentChannel))
		},
		displayCoupons() {
			return this.coupons.filter(coupon => this.isCouponDisplayable(coupon, this.currentChannel))
		},
		rebateCoupons() {
			return this.availableCoupons.filter(item => this.getCouponKind(item) === 'rebate')
		},
		discountCoupons() {
			return this.displayCoupons.filter(item => this.getCouponKind(item) === 'discount')
		},
		selectedCouponKind() {
			return this.selectedCouponObj ? this.getCouponKind(this.selectedCouponObj) : null
		},
		couponKindTabs() {
			return [
				{
					kind: 'discount',
					label: this.getCouponKindLabel('discount'),
					count: this.discountCoupons.length
				},
				{
					kind: 'rebate',
					label: this.getCouponKindLabel('rebate'),
					count: this.rebateCoupons.length
				}
			]
		},
		activeCouponItems() {
			return this.activeCouponKind === 'discount' ? this.discountCoupons : this.rebateCoupons
		},
		couponPickerGroups() {
			const available = []
			const unavailable = []
			this.activeCouponItems.forEach(item => {
				if (this.isCouponApplicable(item, this.currentChannel)) available.push(item)
				else unavailable.push(item)
			})
			return [
				{ key: 'available', items: available, disabled: false },
				{ key: 'unavailable', items: unavailable, disabled: true }
			].filter(group => group.items.length)
		},
		bestCoupon() {
			if (!this.availableCoupons.length) return null
			const recommendedCoupon = this.availableCoupons.find(item => item.recommended === true)
			if (recommendedCoupon) return recommendedCoupon
			return this.availableCoupons.reduce((best, item) => (
				this.getCouponBenefitScore(item) > this.getCouponBenefitScore(best) ? item : best
			))
		},
		showExtraRebateTips() {
			return this.gameData.gameCode === 'sgyjltw'
				&& this.selectedCouponObj
				&& this.getCouponKind(this.selectedCouponObj) === 'rebate'
				&& Math.round(this.toNumber(this.selectedCouponObj.discountValue, 0) * 100) === 50
		},
		finalRebate() {
			return this.getChannelRebate(this.currentChannel)
		},
		recommendedChannels() {
			return this.channelList
				.map((item, index) => ({ ...item, _idx: index }))
				.filter(item => item.isRecommended && (!item.sandbox || this.loginData.isTest))
		},
		otherChannels() {
			return this.channelList
				.map((item, index) => ({ ...item, _idx: index }))
				.filter(item => !item.isRecommended && (!item.sandbox || this.loginData.isTest))
		}
	},
	mounted() {
		this.$analytics.payment.begin({ product_id: this.goods.productId, product_name: this.goods.productName || this.goods.name || this.goods.tips })
		this.initializeCheckoutData()
		this.timer = setInterval(() => {
			this.coupons = this.coupons.map(item => ({
				...item,
				remainingTime: this.toNumber(item.remainingTime, 0) > 0
					? this.toNumber(item.remainingTime, 0) - 1
					: item.remainingTime
			}))
		}, 1000)
		document.body.style.overflow = 'hidden'
		document.documentElement.style.overflow = 'hidden'
	},
	beforeDestroy() {
		if (this.timer) clearInterval(this.timer)
		document.body.style.overflow = ''
		document.documentElement.style.overflow = ''
	},
	methods: {
		initializeCheckoutData() {
			this.checkoutReady = false
			return Promise.all([this.loadChannelList(), this.loadCoupons()]).then(() => {
				this.selectDefaultChannel()
				return this.$nextTick(() => {
					this.reconcileCouponSelection(true)
					this.checkoutReady = true
				})
			}).catch(() => {
				this.checkoutReady = true
			})
		},
		toNumber(value, fallback = null) {
			if (value === null || value === undefined || value === '') return fallback
			const number = Number(value)
			return Number.isFinite(number) ? number : fallback
		},
		sameCouponId(first, second) {
			return String(first) === String(second)
		},
		getCouponKind(coupon) {
			if (!coupon) return 'rebate'
			if (coupon._couponKind === 'discount' || coupon._couponKind === 'rebate') return coupon._couponKind
			const amountField = DISCOUNT_AMOUNT_FIELDS.some(field => this.toNumber(coupon[field]) !== null)
			const kindText = [
				coupon.couponKind,
				coupon.couponType,
				coupon.type,
				coupon.discountType,
				coupon.valueType,
				coupon.couponCategory,
				coupon.category,
				coupon.couponName,
				coupon.name
			].filter(value => value !== undefined && value !== null).join(' ').toLowerCase()
			const isDiscountText = /(discount|deduct|reduc|amount|cash|fixed|full.?reduction|折扣|满减|滿減|抵扣|抵減)/.test(kindText)
			const numericCouponType = this.toNumber(coupon.couponType)
			return amountField || isDiscountText || numericCouponType === 2 ? 'discount' : 'rebate'
		},
		getCouponLabel(coupon) {
			const fallback = this.getCouponKind(coupon) === 'discount'
				? this.$t('discount_coupon_label')
				: this.$t('coupon_booster_label')
			return coupon.couponName || coupon.name || coupon.title || fallback
		},
		getCouponKindLabel(kind) {
			return kind === 'discount' ? this.$t('discount_coupon_label') : this.$t('coupon_booster_label')
		},
		getCouponFixedAmount(coupon) {
			for (const field of DISCOUNT_AMOUNT_FIELDS) {
				const amount = this.toNumber(coupon[field])
				if (amount !== null) return Math.max(0, amount)
			}
			const nestedDiscountAmount = coupon && coupon.discount
				? this.toNumber(coupon.discount.amount)
				: null
			if (nestedDiscountAmount !== null) return Math.max(0, nestedDiscountAmount)
			return Math.max(0, this.toNumber(coupon.discountValue, 0))
		},
		getCouponSavingAmount(coupon) {
			if (!coupon || this.getCouponKind(coupon) !== 'discount') return 0
			return Math.min(this.toNumber(this.goods.amount, 0), this.getCouponFixedAmount(coupon))
		},
		getCouponBenefitScore(coupon) {
			if (!coupon) return 0
			if (this.getCouponKind(coupon) === 'discount') return this.getCouponSavingAmount(coupon)
			return this.toNumber(this.goods.amount, 0) * Math.max(0, this.toNumber(coupon.discountValue, 0))
		},
		getCouponValueText(coupon) {
			if (this.getCouponKind(coupon) === 'discount') {
				return `-${this.goods.currency} ${this.formatMoney(this.getCouponSavingAmount(coupon))}`
			}
			return `+${Math.round(this.toNumber(coupon.discountValue, 0) * 100)}%`
		},
		formatMoney(value) {
			const amount = this.toNumber(value, 0)
			return Number(amount.toFixed(2)).toString()
		},
		isCouponSelected(coupon) {
			return coupon && this.selectedCouponId !== null && this.sameCouponId(coupon.couponId, this.selectedCouponId)
		},
		isBestCoupon(coupon) {
			return coupon && this.bestCoupon && this.sameCouponId(coupon.couponId, this.bestCoupon.couponId)
		},
		isCouponProductExcluded(coupon) {
			if (!coupon) return false
			const currentProductId = String((this.goods && this.goods.productId) || '').trim()
			if (!currentProductId) return false

			const excludedProductIds = []
			const exclusionSources = [coupon.excludedProductIds, coupon.excludedProductId]
			exclusionSources.forEach(value => {
				if (value === null || value === undefined || value === '') return
				const items = Array.isArray(value) ? value : String(value).split(',')
				items.forEach(item => {
					const productId = String(item).trim()
					if (productId) excludedProductIds.push(productId)
				})
			})

			return excludedProductIds.includes(currentProductId)
		},
		normalizeCouponChannels(value) {
			if (value === null || value === undefined || value === '') return []
			const channels = Array.isArray(value) ? value : String(value).split(',')
			return channels
				.map(channel => String(channel).split(':', 1)[0].trim().toLowerCase())
				.filter(Boolean)
		},
		getCouponChannelRestrictions(coupon) {
			if (!coupon) return []
			return [coupon.purchaseChannel, coupon.cashflow]
				.map(value => this.normalizeCouponChannels(value))
				.filter(channels => channels.length)
		},
		isCouponChannelApplicable(coupon, channelItem) {
			const restrictions = this.getCouponChannelRestrictions(coupon)
			if (!restrictions.length) return true
			if (!channelItem) return false

			const currentChannel = typeof channelItem === 'string'
				? channelItem
				: channelItem.channel
			const normalizedChannel = String(currentChannel || '').trim().toLowerCase()
			return !!normalizedChannel && restrictions.every(channels => channels.includes(normalizedChannel))
		},
		getCouponAllowedChannelText(coupon) {
			const channelNames = this.channelList
				.filter(channel => this.isCouponChannelApplicable(coupon, channel))
				.map(channel => String(channel.name || '').trim())
				.filter((name, index, names) => name && names.indexOf(name) === index)
			if (channelNames.length) return channelNames.join(' / ')

			const channelCodes = this.getCouponChannelRestrictions(coupon)
				.reduce((codes, channels) => codes.concat(channels), [])
				.filter((code, index, codes) => codes.indexOf(code) === index)
			return channelCodes.join(' / ')
		},
		isCouponBaseApplicable(coupon) {
			if (!coupon) return false
			const status = String(coupon.status)
			const remainingTime = this.toNumber(coupon.remainingTime)
			if ((remainingTime !== null && remainingTime <= 0) || status === '2' || status === '3') return false

			if (this.getCouponKind(coupon) === 'rebate' && !isRebateApplicable(this.rebate, this.goods)) return false
			return true
		},
		isCouponAmountApplicable(coupon) {
			const price = this.toNumber(this.goods.amount, 0)
			const minAmount = this.toNumber(coupon && coupon.minAmount)
			const maxAmount = this.toNumber(coupon && coupon.maxAmount)
			return (minAmount === null || price >= minAmount) && (maxAmount === null || price <= maxAmount)
		},
		isCouponBelowThreshold(coupon) {
			if (!coupon || this.getCouponKind(coupon) !== 'discount') return false
			const minAmount = this.toNumber(coupon.minAmount)
			return minAmount !== null && this.toNumber(this.goods.amount, 0) < minAmount
		},
		isCouponApplicable(coupon, channelItem) {
			return this.isCouponBaseApplicable(coupon)
				&& !this.isCouponProductExcluded(coupon)
				&& this.isCouponChannelApplicable(coupon, channelItem)
				&& this.isCouponAmountApplicable(coupon)
		},
		isCouponDisplayable(coupon, channelItem) {
			if (!this.isCouponBaseApplicable(coupon)) return false
			const isUnavailableDiscount = this.getCouponKind(coupon) === 'discount'
				&& (this.isCouponProductExcluded(coupon) || !this.isCouponChannelApplicable(coupon, channelItem))
			if (isUnavailableDiscount) return true
			return this.isCouponAmountApplicable(coupon) || this.isCouponBelowThreshold(coupon)
		},
		getCouponPickerHint(coupon) {
			if (this.isCouponProductExcluded(coupon)) return this.$t('coupon_product_excluded')
			if (!this.isCouponChannelApplicable(coupon, this.currentChannel)) {
				return this.$t('coupon_channel_restricted', {
					channels: this.getCouponAllowedChannelText(coupon)
				})
			}
			if (this.isCouponBelowThreshold(coupon)) {
				const minAmount = this.toNumber(coupon.minAmount, 0)
				const price = this.toNumber(this.goods.amount, 0)
				const shortfall = Math.max(0, minAmount - price)
				return this.$t('coupon_threshold_shortfall', {
					amount: `${coupon.currency || this.goods.currency} ${this.formatMoney(shortfall)}`
				})
			}
			return this.$t('coupon_expires_after', { time: this.getCountdown(coupon.remainingTime) })
		},
		couponForChannel(channelItem) {
			if (this.selectedCouponId === null || !channelItem) return null
			const coupon = this.coupons.find(item => this.sameCouponId(item.couponId, this.selectedCouponId))
			return this.isCouponApplicable(coupon, channelItem) ? coupon : null
		},
		getChannelRebate(channelItem) {
			if (!channelItem) return 0
			const selected = this.couponForChannel(channelItem)
			const rebateCoupon = selected && this.getCouponKind(selected) === 'rebate' ? selected : null
			const rebateConfig = isEmpty(this.rebate) ? null : this.rebate
			const result = calculateRebatePercent(rebateConfig, this.goods, rebateCoupon)
			return Number.isFinite(result) ? result : 0
		},
		getChannelBenefitText(channelItem) {
			const selected = this.couponForChannel(channelItem)
			if (selected && this.getCouponKind(selected) === 'discount') {
				const discountAmount = this.getCouponSavingAmount(selected)
				if (discountAmount > 0) {
					return this.$t('payment_order_discount', {
						amount: `${selected.currency || this.goods.currency} ${this.formatMoney(discountAmount)}`
					})
				}
			}

			const rebatePercent = this.getChannelRebate(channelItem)
			return rebatePercent > 0
				? this.$t('payment_order_rebate', { amount: `${this.formatMoney(rebatePercent)}%` })
				: ''
		},
		getCouponResponseItems(response) {
			if (Array.isArray(response)) return response
			if (!response || response.code !== 1000 || !Array.isArray(response.data)) return []
			return response.data
		},
		normalizeRebateCoupon(coupon) {
			return {
				...coupon,
				_couponKind: 'rebate',
				remainingTime: coupon.remainingTime !== undefined
					? coupon.remainingTime
					: coupon.remainingValiditySeconds
			}
		},
		normalizeFullReductionCoupon(coupon) {
			const discount = coupon.discount || {}
			const threshold = coupon.threshold || {}
			const localDiscountAmount = this.toNumber(coupon.localDiscountAmount)
			const minimumLocalAmount = this.toNumber(coupon.minimumLocalAmount)
			return {
				...coupon,
				_couponKind: 'discount',
				remainingTime: coupon.remainingValiditySeconds,
				discountAmount: localDiscountAmount !== null ? localDiscountAmount : discount.amount,
				minAmount: minimumLocalAmount !== null ? minimumLocalAmount : threshold.amount,
				currency: coupon.localCurrency || discount.currency || this.goods.currency
			}
		},
		mergeCoupons(rebateCoupons, fullReductionCoupons) {
			const couponMap = new Map()
			const allCoupons = [...rebateCoupons, ...fullReductionCoupons]
			allCoupons.forEach(coupon => {
				if (!coupon || coupon.couponId === undefined || coupon.couponId === null) return
				couponMap.set(String(coupon.couponId), coupon)
			})
			return Array.from(couponMap.values())
		},
		loadCoupons() {
			this.couponLoading = true
			const commonParams = {
				userId: this.$session.getUserId(),
				gameCode: this.$cache.getGameCode()
			}
			const rebateRequest = this.$axios({
				method: 'get',
				url: '/web/api/coupon/getUserCoupons',
				params: commonParams,
				silentError: true
			}).then(response => this.getCouponResponseItems(response).map(item => this.normalizeRebateCoupon(item)))
				.catch(() => [])
			const fullReductionRequest = this.isSuperCouponEnabled
				? this.$axios({
					method: 'get',
					url: '/web/api/coupon/getAvailableFullReductionCoupons',
					params: {
						...commonParams,
						productId: this.goods.productId,
						amount: this.goods.amount,
						currency: this.goods.currency
					},
					silentError: true
				}).then(response => this.getCouponResponseItems(response).map(item => this.normalizeFullReductionCoupon(item)))
					.catch(() => [])
				: Promise.resolve([])

			return Promise.all([rebateRequest, fullReductionRequest])
				.then(([rebateCoupons, fullReductionCoupons]) => {
					this.coupons = this.mergeCoupons(rebateCoupons, fullReductionCoupons)
					if (this.coupon && this.coupon.couponId && !this.coupons.some(item => this.sameCouponId(item.couponId, this.coupon.couponId))) {
						this.coupons.push(this.normalizeRebateCoupon(this.coupon))
					}
				})
				.catch(() => {
					this.coupons = []
					this.selectedCouponId = null
				})
				.then(() => {
					this.couponLoading = false
				})
		},
		reconcileCouponSelection(allowAutoSelect) {
			if (!this.couponSelectionTouched && allowAutoSelect) {
				this.selectedCouponId = this.bestCoupon ? this.bestCoupon.couponId : null
				return
			}
			const stillAvailable = this.availableCoupons.some(item => this.sameCouponId(item.couponId, this.selectedCouponId))
			if (!stillAvailable) this.selectedCouponId = null
		},
		selectDefaultChannel() {
			const visible = item => !item.sandbox || this.loginData.isTest
			const recommendedIndex = this.channelList.findIndex(item => item.isRecommended && visible(item))
			const fallbackIndex = this.channelList.findIndex(visible)
			this.selectedChannel = recommendedIndex >= 0 ? recommendedIndex : fallbackIndex
		},
		openCouponPicker() {
			this.$analytics.track('coupon_picker_opened', { product_id: this.goods.productId, available_count: this.availableCoupons.length })
			const selectedKind = this.selectedCouponKind
			if (selectedKind) {
				this.activeCouponKind = selectedKind
			} else if (this.activeCouponKind === 'discount' && !this.discountCoupons.length) {
				this.activeCouponKind = 'rebate'
			} else if (this.activeCouponKind === 'rebate' && !this.rebateCoupons.length) {
				this.activeCouponKind = 'discount'
			}
			this.showCouponPicker = true
		},
		setActiveCouponKind(kind) {
			const hasCoupons = kind === 'discount' ? this.discountCoupons.length : this.rebateCoupons.length
			if (hasCoupons) this.activeCouponKind = kind
		},
		closeCouponPicker() {
			this.showCouponPicker = false
		},
		selectCoupon(item) {
			if (!item || !this.isCouponApplicable(item, this.currentChannel)) return
			this.couponSelectionTouched = true
			if (this.isCouponSelected(item)) {
				this.selectedCouponId = null
				return
			}
			this.selectedCouponId = item.couponId
			this.$analytics.track('coupon_selected', { product_id: this.goods.productId, coupon_kind: this.getCouponKind(item) })
			this.closeCouponPicker()
		},
		getCountdown(remainingSeconds) {
			const seconds = Math.floor(this.toNumber(remainingSeconds, 0))
			if (seconds <= 0) return this.$t('coupon_expired_text')
			const hours = Math.floor(seconds / 3600)
			const minutes = Math.floor((seconds % 3600) / 60)
			const restSeconds = seconds % 60
			if (hours >= 24) {
				const days = Math.floor(hours / 24)
				const restHours = hours % 24
				return `${days}${this.$t('rebate_countdown_days')} ${String(restHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(restSeconds).padStart(2, '0')}`
			}
			return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(restSeconds).padStart(2, '0')}`
		},
		loadChannelList() {
			this.channelLoading = true
			return axios.get(`/static/json/payment/${this.$cache.getGameCode()}.json?v${Date.now()}`)
				.then(response => {
					this.srcChannelList = Array.isArray(response.data) ? response.data : []
					this.channelList = this.filterChannelList(this.srcChannelList)
				})
				.catch(() => {
					this.srcChannelList = []
					this.channelList = []
					this.selectedChannel = -1
				})
				.then(() => {
					this.channelLoading = false
				})
		},
		filterChannelList(data) {
			const loginData = this.$cache.getLoginData()
			if (isEmpty(loginData)) {
				this.$session.clearCookieLoginData()
				window.location.reload()
				return []
			}
			const amount = this.toNumber(this.goods.amount, 0)
			return (Array.isArray(data) ? data : []).filter(item => {
				if (!this.goods.isInGame && item.paymentType === 'INGAME') return false
				const min = this.toNumber(item.min)
				const max = this.toNumber(item.max)
				return (min === null || amount >= min) && (max === null || amount <= max)
			})
		},
		chooseChannel(index) {
			this.selectedChannel = index
			const channel = this.currentChannel || {}
			this.$analytics.track('payment_method_selected', { product_id: this.goods.productId, payment_channel: channel.channel, payment_type: channel.paymentType })
			this.$nextTick(() => this.reconcileCouponSelection(true))
		},
		closeWindow() {
			const channel = this.currentChannel || {}
			this.$analytics.track('checkout_cancelled', {
				game_code: this.gameData.gameCode || this.$cache.getGameCode() || null,
				product_id: this.goods.productId || null,
				amount: Number.isFinite(Number(this.goods.amount)) ? Number(this.goods.amount) : null,
				currency: this.goods.currency || null,
				payment_channel: channel.channel || null,
				payment_type: channel.paymentType || null
			})
			if (typeof window !== 'undefined' && window.gtag && this.gameData.gameCode === 'msgen') {
				window.gtag('event', 'Cancel_payment', {
					game: this.gameData.gameCode,
					goods: this.goods.productId
				})
			}
			this.$emit('close')
		},
		openThirdPaymentPage() {
			const channel = this.currentChannel
			if (!channel || this.buttonDisabled) return

			this.buttonDisabled = true
			this.$setLoading('confirmButton', true)
			try {
				const roleInfo = this.$cache.getRoleInfo()
				const selectedCoupon = this.couponForChannel(channel)
				const canUseSelectedCoupon = selectedCoupon && (
					this.getCouponKind(selectedCoupon) === 'discount' || isRebateApplicable(this.rebate, this.goods)
				)
				openThirdPartyPayment({
					gameData: this.gameData,
					roleInfo,
					goods: this.goods,
					channel,
					couponId: canUseSelectedCoupon ? selectedCoupon.couponId : ''
				})
			} catch (error) {
				this.$analytics.track('checkout_blocked', { product_id: this.goods.productId, reason: 'payment_initialization_failed' })
				this.buttonDisabled = false
				this.$setLoading('confirmButton', false)
				console.error('[confirmOrder] Failed to initialize payment', error)
			}
		}
	}
}
</script>

<style scoped src="../assets/css/confirmOrder.css"></style>
